import { memo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Divider,
  Stack,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AttachmentIcon from "@mui/icons-material/Attachment";
import ModernCard from "./ModernCard";
import PropTypes from "prop-types";

// Parse Boolean/YES/NO Utility
// Backend sends "true" or "false" as strings, we display as YES/NO
const parseBooleanOrYesNo = (value) => {
  if (typeof value === "string") {
    const lowerValue = value.toLowerCase().trim();
    // Handle empty string - no selection
    if (lowerValue === "") return null;
    // Handle new format: "true" -> YES, "false" -> NO
    if (lowerValue === "true" || lowerValue === "yes") return "YES";
    if (lowerValue === "false" || lowerValue === "no") return "NO";
  }
  if (typeof value === "boolean") {
    return value ? "YES" : "NO";
  }
  return null;
};

// Parse Confirmation (always returns boolean)
// Backend sends "true" or "false" as strings, we display as checked/unchecked
const parseConfirmation = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lowerValue = value.toLowerCase();
    return lowerValue === "true" || lowerValue === "yes";
  }
  return false;
};

// Helper to map user IDs to names
const getUserDisplayValue = (rawValue, UsersList, isMulti = false, t) => {
  if (!rawValue) return t("documentDetail.na");

  if (isMulti) {
    if (!Array.isArray(rawValue)) {
      return JSON.stringify(rawValue) || t("documentDetail.na");
    }
    return (
      rawValue
        .map((userId) => {
          const user = UsersList.find((u) => u.value === userId);
          return user ? user.label : userId;
        })
        .join(", ") || t("documentDetail.na")
    );
  }

  const user = UsersList.find((u) => u.value === rawValue);
  return user ? user.label : rawValue || t("documentDetail.na");
};

const TimelineSection = memo(
  ({ historySteps, convertUTCToLocal, onFileClick, UsersList, t }) => {
    const theme = useTheme();
    const [expandedSteps, setExpandedSteps] = useState({});

    const handleToggleStep = (stepId) => {
      setExpandedSteps((prev) => ({
        ...prev,
        [stepId]: !prev[stepId],
      }));
    };

    return (
      <ModernCard
        title={t("documentDetail.actionsTimelineTitle")}
        icon={AssignmentIcon}
        color="success"
      >
        {historySteps.length > 0 ? (
          <Timeline
            sx={{
              m: 0,
              p: 0,
              "& .MuiTimelineItem-root": {
                minHeight: "auto",
                "&:before": {
                  display: "none",
                },
              },
              "& .MuiTimelineSeparator-root": {
                mt: "12px",
                mr: 3,
              },
              "& .MuiTimelineContent-root": {
                py: 1,
                pt: 2,
                px: 0,
                width: "100%",
              },
            }}
          >
            {historySteps.map((step, index) => (
              <TimelineItem key={step.step_id}>
                <TimelineSeparator>
                  <TimelineDot
                    color="success"
                    sx={{
                      boxShadow: 3,
                      border: (theme) =>
                        `2px solid ${theme.palette.background.paper}`,
                    }}
                  />
                  {index < historySteps.length - 1 && (
                    <TimelineConnector sx={{ bgcolor: "success.main" }} />
                  )}
                </TimelineSeparator>
                <TimelineContent>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        boxShadow: 2,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                        onClick={() => handleToggleStep(step.step_id)}
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="success.main"
                          >
                            {t("documentDetail.stepStatus", {
                              status: step.status,
                              firstName: step.first_name,
                              lastName: step.last_name,
                            })}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <AccessTimeIcon
                              sx={{
                                fontSize: 16,
                                mr: 0.5,
                                verticalAlign: "middle",
                              }}
                            />
                            {convertUTCToLocal(step.created_at)}
                          </Typography>
                        </Box>
                        <IconButton size="small" color="primary">
                          {expandedSteps[step.step_id] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </Box>
                      <Collapse in={expandedSteps[step.step_id]}>
                        <Divider sx={{ my: 2 }} />
                        {step.step_input_values &&
                        step.step_input_values.length > 0 ? (
                          <Stack spacing={1}>
                            {step.step_input_values.map((input) => (
                              <Box
                                key={input.id}
                                sx={{
                                  p: 1,
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                }}
                              >
                                {input.input_type === "BOOLEAN" ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {input.name}:
                                    </Typography>
                                    <ToggleButtonGroup
                                      value={parseBooleanOrYesNo(
                                        input.raw_value
                                      )}
                                      exclusive
                                      size="small"
                                      sx={{
                                        "& .MuiToggleButton-root": {
                                          px: 2,
                                          py: 0.5,
                                          textTransform: "none",
                                          fontSize: "0.875rem",
                                          fontWeight: 600,
                                          minWidth: 50,
                                          border: `1px solid ${theme.palette.divider}`,
                                          "&.Mui-selected": {
                                            fontWeight: 700,
                                            "&[value='YES']": {
                                              bgcolor:
                                                theme.palette.success.main,
                                              color:
                                                theme.palette.success
                                                  .contrastText,
                                              "&:hover": {
                                                bgcolor:
                                                  theme.palette.success.dark,
                                              },
                                            },
                                            "&[value='NO']": {
                                              bgcolor: theme.palette.error.main,
                                              color:
                                                theme.palette.error
                                                  .contrastText,
                                              "&:hover": {
                                                bgcolor:
                                                  theme.palette.error.dark,
                                              },
                                            },
                                          },
                                          "&.Mui-disabled": {
                                            border: `1px solid ${theme.palette.divider}`,
                                          },
                                        },
                                      }}
                                      disabled
                                    >
                                      <ToggleButton
                                        value="YES"
                                        aria-label="yes"
                                      >
                                        {t("documentDetail.yes", "YES")}
                                      </ToggleButton>
                                      <ToggleButton value="NO" aria-label="no">
                                        {t("documentDetail.no", "NO")}
                                      </ToggleButton>
                                    </ToggleButtonGroup>
                                  </Box>
                                ) : input.input_type === "CONFIRMATION" ? (
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={parseConfirmation(
                                          input.raw_value
                                        )}
                                        disabled
                                        color="primary"
                                        sx={{
                                          "&.Mui-checked": {
                                            color: theme.palette.success.main,
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Typography variant="body2">
                                        {input.name}
                                      </Typography>
                                    }
                                  />
                                ) : input.input_type === "FILE_UPLOAD" ? (
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                      sx={{ mb: 1 }}
                                    >
                                      {input.name}:
                                    </Typography>
                                    {input.attachments &&
                                    input.attachments.length > 0 ? (
                                      <Box
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns:
                                            "repeat(auto-fill, minmax(100px, 1fr))",
                                          gap: 1.5,
                                        }}
                                      >
                                        {input.attachments.map((attachment) => (
                                          <Box
                                            key={attachment.id}
                                            onClick={() =>
                                              onFileClick(attachment.url)
                                            }
                                            sx={{
                                              cursor: "pointer",
                                              borderRadius: 2,
                                              overflow: "hidden",
                                              border: "1px solid",
                                              borderColor: "divider",
                                              transition:
                                                "all 0.2s ease-in-out",
                                              "&:hover": {
                                                borderColor: "primary.main",
                                                transform: "translateY(-2px)",
                                                boxShadow: 2,
                                              },
                                            }}
                                          >
                                            {attachment.is_image ? (
                                              <Box>
                                                <img
                                                  src={attachment.url}
                                                  alt={
                                                    attachment.display_name ||
                                                    t("documentDetail.imageAlt")
                                                  }
                                                  loading="lazy"
                                                  style={{
                                                    objectFit: "cover",
                                                    width: "100%",
                                                    height: "80px",
                                                    borderRadius: "8px 8px 0 0",
                                                  }}
                                                />
                                                <Box sx={{ p: 1 }}>
                                                  <Typography
                                                    variant="caption"
                                                    sx={{
                                                      display: "block",
                                                      fontWeight: 500,
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {attachment.display_name ||
                                                      t(
                                                        "documentDetail.imageLabel"
                                                      )}
                                                  </Typography>
                                                </Box>
                                              </Box>
                                            ) : (
                                              <Box
                                                sx={{
                                                  textAlign: "center",
                                                  p: 1.5,
                                                }}
                                              >
                                                <AttachmentIcon
                                                  sx={{
                                                    fontSize: 24,
                                                    color: "text.secondary",
                                                    mb: 0.5,
                                                  }}
                                                />
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    display: "block",
                                                    fontWeight: 500,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {attachment.display_name ||
                                                    t(
                                                      "documentDetail.fileLabel"
                                                    )}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {t("documentDetail.noFilesUploaded")}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : [
                                    "MULTI_USER_SELECTION",
                                    "USER_SELECTION",
                                  ].includes(input.input_type) ? (
                                  <Typography variant="body2">
                                    <strong>{input.name}:</strong>{" "}
                                    {getUserDisplayValue(
                                      input.raw_value,
                                      UsersList,
                                      input.input_type ===
                                        "MULTI_USER_SELECTION",
                                      t
                                    )}
                                  </Typography>
                                ) : (
                                  <Typography variant="body2">
                                    <strong>{input.name}:</strong>{" "}
                                    {input.input_type === "TIMESTAMP"
                                      ? convertUTCToLocal(input.raw_value)
                                      : input.input_type === "NUMERIC"
                                        ? input.raw_value
                                        : input.input_type ===
                                              "MULTI_SELECTION" &&
                                            Array.isArray(input.raw_value)
                                          ? input.raw_value.join(", ")
                                          : input.input_type === "SELECTION" ||
                                              input.input_type === "SHORT_TEXT"
                                            ? input.raw_value
                                            : typeof input.raw_value ===
                                                "object"
                                              ? JSON.stringify(input.raw_value)
                                              : input.raw_value ||
                                                t("documentDetail.na")}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t("documentDetail.noInputsForStep")}
                          </Typography>
                        )}
                      </Collapse>
                    </CardContent>
                  </Card>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        ) : (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <AssignmentIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary">
              {t("documentDetail.noActionsAvailable")}
            </Typography>
          </Box>
        )}
      </ModernCard>
    );
  }
);

TimelineSection.displayName = "TimelineSection";

TimelineSection.propTypes = {
  historySteps: PropTypes.array.isRequired,
  convertUTCToLocal: PropTypes.func.isRequired,
  onFileClick: PropTypes.func.isRequired,
  UsersList: PropTypes.array.isRequired,
  t: PropTypes.func.isRequired,
};

export default TimelineSection;
